// v3.94 - MISSED-ID-04 EDGS: AI Transparency page - 2026-04-25
//
// Static page explaining how MyLojic uses AI, what safeguards are in place,
// and the Employment Decision Guardrail System. Linked from sidebar footer.

import React from 'react';
import {
    Box,
    Typography,
    Divider,
    Paper,
    List,
    ListItem,
    ListItemText,
    Chip,
    Stack,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

export default function AiTransparency() {
    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h4" fontWeight={700} mb={0.5}>
                AI Transparency
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
                MyLojic uses AI to help you understand your business data. This page explains how our AI
                works, what guardrails we have in place, and what we do — and don't do — with your data.
            </Typography>

            <Divider sx={{ mb: 3 }} />

            {/* How AI is used */}
            <Typography variant="h6" fontWeight={600} mb={1}>
                How we use AI
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Our AI answers plain-language questions about your connected business data (QuickBooks, Xero,
                and others). It reads your financial and operational data, generates answers grounded in
                your actual numbers, and cites the source tables it used.
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
                AI is also used during the data pipeline to categorize and clean your raw data before
                building your dashboards. All AI-generated insights are tied to real data — we do not
                hallucinate numbers.
            </Typography>

            <Divider sx={{ mb: 3 }} />

            {/* EDGS */}
            <Typography variant="h6" fontWeight={600} mb={1}>
                Employment Decision Guardrail System (EDGS)
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
                We automatically classify every question that involves workforce data. This protects
                against answers that could inappropriately influence decisions about individual employees.
                The system is universal — it applies to all accounts regardless of location.
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                <Chip icon={<CheckCircleOutlineIcon />} label="LOW risk — no action" color="success" variant="outlined" size="small" />
                <Chip icon={<WarningAmberIcon />} label="MEDIUM — disclaimer shown" color="warning" size="small" />
                <Chip icon={<BlockIcon />} label="HIGH — answer reframed + acknowledgement required" color="error" size="small" />
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <List dense disablePadding>
                    {[
                        ['LOW', 'Aggregate financial data with no individual-level risk (e.g., total labor cost as % of revenue).'],
                        ['MEDIUM', 'Workforce aggregate data that could be misused (e.g., payroll by department). A disclaimer is shown inline.'],
                        ['HIGH', 'Data that could directly influence a decision about a specific person (e.g., who costs the most). The answer is rewritten to aggregate level and you must acknowledge before viewing.'],
                    ].map(([level, desc]) => (
                        <ListItem key={level} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
                            <ListItemText
                                primary={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip
                                            label={level}
                                            size="small"
                                            color={level === 'HIGH' ? 'error' : level === 'MEDIUM' ? 'warning' : 'success'}
                                            variant={level === 'LOW' ? 'outlined' : 'filled'}
                                            sx={{ width: 72 }}
                                        />
                                        <Typography variant="body2">{desc}</Typography>
                                    </Stack>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </Paper>

            <Divider sx={{ mb: 3 }} />

            {/* What we don't do */}
            <Typography variant="h6" fontWeight={600} mb={1}>
                What we never do
            </Typography>
            <List dense>
                {[
                    'Store your raw questions — only a cryptographic hash is retained for audit purposes.',
                    'Make employment decisions — all AI outputs are informational only.',
                    'Share your data with other customers.',
                    'Use your data to train AI models.',
                    'Provide legal, HR, or compliance advice.',
                ].map((item) => (
                    <ListItem key={item} disableGutters sx={{ py: 0.25 }}>
                        <BlockIcon sx={{ fontSize: 14, color: 'error.main', mr: 1, flexShrink: 0 }} />
                        <Typography variant="body2">{item}</Typography>
                    </ListItem>
                ))}
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="caption" color="text.disabled">
                MyLojic AI Transparency — last updated April 2026.
                Employment Decision Guardrail System governed by Washington State AI Act (SB 5453).
            </Typography>
        </Box>
    );
}
